'use client';

import { useState } from 'react';
import { ChevronDown, ChevronUp } from '@/lib/icons';
import { cn } from '@/lib/utils';

interface FAQ {
  question: string;
  answer: string;
  schema?: boolean;
}

interface FAQSectionProps {
  faqs: FAQ[];
}

export function FAQSection({ faqs }: FAQSectionProps) {
  const [openItems, setOpenItems] = useState<number[]>([0]); // First item open by default

  // Schema is now handled by the main page template

  const toggleItem = (index: number) => {
    setOpenItems(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  if (faqs.length === 0) {
    return null;
  }

  return (
    <section className="mt-12 pt-12 border-t border-gray-200">
      <h2 className="text-3xl font-bold text-gray-900 mb-8">
        Frequently Asked Questions
      </h2>
      
      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <div
            key={index}
            className="border border-gray-200 rounded-lg overflow-hidden"
          >
            <button
              onClick={() => toggleItem(index)}
              className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
              aria-expanded={openItems.includes(index)}
              aria-controls={`faq-answer-${index}`}
            >
              <h3 className="text-lg font-medium text-gray-900 pr-4">
                {faq.question}
              </h3>
              {openItems.includes(index) ? (
                <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
              )}
            </button>
            
            <div
              id={`faq-answer-${index}`}
              className={cn(
                'px-6 overflow-hidden transition-all duration-300',
                openItems.includes(index) ? 'py-4' : 'max-h-0'
              )}
            >
              <div className="text-gray-700 prose prose-sm max-w-none">
                {faq.answer}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}